import React, { useEffect, useState } from "react";
import { Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";

const statusOptions = [
  { value: "requested", label: "Requested" },
  { value: "reviewing", label: "Reviewing" },
  { value: "completed", label: "Completed" },
  { value: "declined", label: "Declined" },
];

export const AccountDeletionRequestsTab = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/admin/account-deletion-requests")
      .then((response) => setRequests(response.data))
      .catch((error) => toast.error(formatError(error)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (request, status) => {
    setSavingId(request.id);
    try {
      const { data } = await api.put(`/admin/account-deletion-requests/${request.id}`, { status });
      setRequests((current) => current.map((item) => (item.id === request.id ? data : item)));
      toast.success("Deletion request status updated");
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSavingId("");
    }
  };

  if (loading) {
    return (
      <div
        className="py-16 text-center text-[#64748B]"
        data-testid="admin-deletion-requests-loading"
      >
        Loading requests…
      </div>
    );
  }

  return (
    <section data-testid="admin-deletion-requests-tab">
      <div className="mb-6 flex items-start gap-3">
        <ShieldAlert size={24} className="mt-1 text-[#2D5A47]" aria-hidden="true" />
        <div>
          <h2 className="font-serif text-3xl font-semibold text-[#1E2A24]">
            Account Deletion Requests
          </h2>
          <p className="mt-1 text-sm text-[#64748B]">
            Review customer requests before any account or personal-data action is taken.
          </p>
        </div>
      </div>
      {requests.length === 0 ? (
        <div
          className="border border-[#E2DCD2] bg-white p-8 text-center text-sm text-[#64748B]"
          data-testid="admin-deletion-requests-empty"
        >
          No account deletion requests yet.
        </div>
      ) : (
        <div
          className="overflow-hidden border border-[#E2DCD2] bg-white"
          data-testid="admin-deletion-requests-list"
        >
          {requests.map((request) => (
            <article
              key={request.id}
              className={[
                "flex flex-col gap-4 border-b border-[#F0ECE5] p-5 last:border-b-0",
                "md:flex-row md:items-center md:justify-between",
              ].join(" ")}
              data-testid={`admin-deletion-request-${request.id}`}
            >
              <div className="min-w-0">
                <h3 className="font-semibold text-[#1E2A24]">{request.name}</h3>
                <p className="mt-1 break-all text-sm text-[#64748B]">{request.email}</p>
                <p className="mt-2 text-xs text-[#8C6D46]">
                  Requested {new Date(request.requested_at).toLocaleString("en-IN")}
                </p>
              </div>
              <label className="flex items-center gap-3 text-sm font-medium text-[#1E2A24]">
                <span>Status</span>
                <select
                  value={request.status}
                  disabled={savingId === request.id}
                  onChange={(event) => updateStatus(request, event.target.value)}
                  className="border border-[#E2DCD2] bg-white px-3 py-2 text-sm"
                  data-testid={`admin-deletion-request-status-${request.id}`}
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                {savingId === request.id && (
                  <Loader2 size={16} className="animate-spin text-[#2D5A47]" />
                )}
              </label>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};